import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DatePicker, Input } from "antd";
import dayjs from "dayjs";
import { useUserAllOrders } from "../../../../../api/orders/orders";

const { RangePicker } = DatePicker;
const ORDERS_PER_PAGE = 20;

const UserOrders = () => {
  const { userId } = useParams(); // from route: /admin/user-orders/:userId
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const startDate = dateRange?.[0]
    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
    : "";
  const endDate = dateRange?.[1]
    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
    : "";

  const { data, isLoading, isFetching, isError } = useUserAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    search: debouncedSearchText,
    startDate,
    endDate,
    usersid: userId, // ⭐ admin viewing user orders
  });

  const orders = data?.orders || [];
  const totalPages = data?.totalPages || 1;

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching user orders</p>;

  return (
    <div className="p-2">
      <h2 className="text-xl font-bold mb-4">Orders for User</h2>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <Input
          className="w-64"
          placeholder="Search by AWB, Order Number or Name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <RangePicker
          className="w-80"
          format="YYYY-MM-DD"
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates || [null, null]);
            setCurrentPage(1);
          }}
          allowClear
        />

        <button
          onClick={() => {
            setSearch("");
            setDateRange([]);
            setCurrentPage(1);
          }}
          className="px-4 py-1 text-base rounded-lg bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white transition-colors"
        >
          Reset
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading orders...</p>
      ) : orders.length === 0 ? (
        <p className="text-gray-500">No orders found.</p>
      ) : (
        <div className="overflow-x-auto hide-scrollbar">
          <table className="min-w-[1100px] w-full table-auto border border-gray-200">
            <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
              <tr>
                <th className="p-3">Order No</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Invoice</th>
                <th className="p-3">Payment</th>
                <th className="p-3">Status</th>
                <th className="p-3">Date</th>
                <th className="p-3">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white text-sm">
              {orders.map((order) => (
                <React.Fragment key={order._id}>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-blue-600">
                      {order.OrderNumber}
                    </td>
                    <td className="p-3">{order.CustomerName}</td>
                    <td className="p-3">₹{order.InvoiceAmount}</td>
                    <td className="p-3 uppercase">{order.PaymentType}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-600">
                        {order.shippingStatusText}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() =>
                          setExpandedRow(
                            expandedRow === order._id ? null : order._id
                          )
                        }
                        className="text-sm text-primary underline"
                      >
                        {expandedRow === order._id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>

                  {expandedRow === order._id && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          {/* Billing */}
                          <div>
                            <h3 className="font-semibold mb-2">
                              Billing Address
                            </h3>
                            <p>{order?.BillingAddress?.AddressLine1 || "-"}</p>
                            <p>
                              {order?.BillingAddress?.City || "-"},{" "}
                              {order?.BillingAddress?.State || "-"}
                            </p>
                            <p>{order?.BillingAddress?.Pincode || "-"}</p>
                            <p>{order?.BillingAddress?.Email || "-"}</p>
                            <p>{order?.BillingAddress?.Contact || "-"}</p>
                          </div>

                          {/* Shipping */}
                          <div>
                            <h3 className="font-semibold mb-2">
                              Shipping Address
                            </h3>
                            <p>{order?.ShippingAddress?.AddressLine1 || "-"}</p>
                            <p>
                              {order?.ShippingAddress?.City || "-"},{" "}
                              {order?.ShippingAddress?.State || "-"}
                            </p>
                            <p>{order?.ShippingAddress?.Pincode || "-"}</p>
                            <p>{order?.ShippingAddress?.Email || "-"}</p>
                            <p>{order?.ShippingAddress?.Contact || "-"}</p>
                          </div>

                          {/* Pickup */}
                          <div>
                            <h3 className="font-semibold mb-2">
                              Pickup Address
                            </h3>
                            <p>{order?.PickupAddress?.AddressLine1 || "-"}</p>
                            <p>
                              {order?.PickupAddress?.City || "-"},{" "}
                              {order?.PickupAddress?.State || "-"}
                            </p>
                            <p>{order?.PickupAddress?.Pincode || "-"}</p>
                            <p>{order?.PickupAddress?.Contact || "-"}</p>
                          </div>

                          {/* Product Details */}
                          <div className="md:col-span-3">
                            <h3 className="font-semibold mb-2">Products</h3>
                            <table className="w-full border">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="p-2">Name</th>
                                  <th className="p-2">SKU</th>
                                  <th className="p-2">QTY</th>
                                  <th className="p-2">Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order?.ProductDetails?.map((p) => (
                                  <tr key={p._id} className="border-t">
                                    <td className="p-2">{p.name}</td>
                                    <td className="p-2">{p.sku}</td>
                                    <td className="p-2">{p.units}</td>
                                    <td className="p-2">₹{p.price}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Charges */}
                          <div>
                            <h3 className="font-semibold mb-2">Charges</h3>
                            <p>Shipping: ₹{order?.ShippingCharge || 0}</p>
                            <p>Discount: ₹{order?.Discount || 0}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!debouncedSearchText && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className="px-4 py-2 rounded border bg-white hover:bg-gray-50 disabled:bg-gray-100"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages || isFetching}
              className="px-4 py-2 rounded border bg-white hover:bg-gray-50 disabled:bg-gray-100"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserOrders;
